// tabs/FacilitatorTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Group, Text, Button, Divider, ActionIcon, Badge,
  SimpleGrid, Card, Avatar, Stack, Tooltip
} from '@mantine/core';
import { IconPlus, IconTrash,IconRefresh } from '@tabler/icons-react';
import { Select } from '@/components';
import {
  useACCourseService, useFacilitatorService,
  CourseFacilitatorView, CourseFacilitatorRequest, FacilitatorModelView,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import ConfirmActionModal from './ConfirmActionModal';

// ─── Props ────────────────────────────────────────────────────────────────────
interface FacilitatorTabProps {
  courseId: number;
  active: boolean;
  onCourseChange?: () => void;
}

// ─── FacilitatorTab ───────────────────────────────────────────────────────────
// Lists all facilitators assigned to a course.
// Add via dropdown (searches all facilitators), remove via confirm modal.
// Uses facilitatorCourseID (join-table row ID) for remove — not userId.
export default function FacilitatorTab({ courseId, active, onCourseChange }: FacilitatorTabProps) {
  const courseService      = useACCourseService();
  const facilitatorService = useFacilitatorService();

  // Facilitators assigned to this course
  const [assigned, setAssigned]       = useState<CourseFacilitatorView[]>([]);
  // Full facilitator list for the add-dropdown
  const [allFacilitators, setAllFacilitators] = useState<FacilitatorModelView[]>([]);
  const [loading, setLoading]         = useState(false);

  // Dropdown selection state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Confirm-remove modal state
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  // ── Fetch all facilitators once for the dropdown ───────────────────────────
  useEffect(() => {
    facilitatorService.list().then(setAllFacilitators).catch(console.error);
  }, [facilitatorService]);

  // ── Fetch assigned facilitators ────────────────────────────────────────────
  const fetchAssigned = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const result = await courseService.getFacilitators(courseId);
      setAssigned(result);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load facilitators.');
    } finally {
      setLoading(false);
    }
  }, [courseId, courseService]);

  // ── Only fetch when tab is active ──────────────────────────────────────────
  useEffect(() => {
    if (active) fetchAssigned();
  }, [active, fetchAssigned]);

  // ── Resolve display name from CourseFacilitatorView ───────────────────────
  const resolveLabel = (f: CourseFacilitatorView): string => {
    const fromParts = [f.firstName, f.surname].filter(Boolean).join(' ').trim();
    if (fromParts) return fromParts;
    return f.displayName ?? f.fullName ?? f.userId;
  };

  // ── userIds already assigned — used to filter the dropdown ────────────────
  const assignedUserIds = new Set(assigned.map((f) => f.userId));

  // ── Add facilitator ────────────────────────────────────────────────────────
  const handleAddFacilitator = async () => {
    if (!selectedUserId || !courseId) return;

    // Guard: prevent duplicate
    if (assignedUserIds.has(selectedUserId)) {
      toast.error('Duplicate', 'This facilitator is already assigned.');
      return;
    }

    setLoading(true);
    try {
      const payload: CourseFacilitatorRequest = { userId: selectedUserId, isActive: true };
      await courseService.addFacilitator(courseId, payload);
      setSelectedUserId(null);
      await fetchAssigned();
      onCourseChange?.();
      toast.success('Facilitator Added', 'Facilitator assigned to course.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to add facilitator.');
    } finally {
      setLoading(false);
    }
  };

  // ── Remove facilitator — opens confirm modal with join-table row ID ────────
  const handleRemoveFacilitator = (f: CourseFacilitatorView) => {
    const name = resolveLabel(f);
    setConfirmMessage(`Remove ${name} from this course?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        // Pass facilitatorCourseID (join-table row), NOT userId
        await courseService.removeFacilitator(courseId, f.facilitatorCourseID);
        await fetchAssigned();
        onCourseChange?.();
        toast.warning('Facilitator Removed', `${name} has been removed.`);
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove facilitator.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Confirm modal */}
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove Facilitator"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      {/* Add facilitator row */}
      <Group gap="sm" justify="space-between" mb="md">
        <Select
          placeholder="Search for a facilitator"
          style={{ width: 320 }}
          data={allFacilitators
            .filter((f) => !assignedUserIds.has(f.userId))
            .map((f) => {
              // Resolve display name from FacilitatorModelView
              const fromParts = [f.firstName, f.surname].filter(Boolean).join(' ').trim();
              const label = fromParts || f.displayName?.trim() || f.fullName?.trim() || f.userId;
              return { value: f.userId, label };
            })}
          value={selectedUserId}
          onChange={setSelectedUserId}
          searchable
          clearable
        />
        <Group gap="xs">
           <Tooltip label="Refresh" withArrow>
                              <ActionIcon
                                variant="subtle"
                                onClick={() => fetchAssigned()}
                                loading={loading}
                                size="lg"
                              >
                                <IconRefresh size={16} />
                              </ActionIcon>
                            </Tooltip>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={handleAddFacilitator}
          disabled={!selectedUserId || loading}
          loading={loading}
        >
          Add Facilitator
        </Button>
        </Group>
      </Group>

      <Divider mb="md" />

      {/* Assigned facilitator cards */}
      {assigned.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No facilitators assigned.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {assigned.map((f) => {
            const name = resolveLabel(f);
            return (
              <Card key={f.facilitatorCourseID} withBorder radius="md" padding="lg">
                <Group justify="flex-end" mb="xs">
                  {/* Status badge colored by facilitator status */}
                  <Badge
                    variant="light"
                    style={
                      f.statusColor
                        ? { backgroundColor: `${f.statusColor}22`, color: f.statusColor }
                        : undefined
                    }
                  >
                    {f.statusName ?? (f.isActive ? 'Active' : 'Inactive')}
                  </Badge>
                </Group>

                {/* Avatar centered */}
                <Group justify="center" mb="sm">
                  <Avatar src={f.avatar ?? undefined} alt={name} size="xl" radius="md">
                    {!f.avatar && name.charAt(0).toUpperCase()}
                  </Avatar>
                </Group>

                {/* Name */}
                <Text fw={700} ta="center" size="md" mb={4}>{name}</Text>

                {/* Contact details */}
                <Stack align='center' gap={2}>
                  {f.email && <Text size="sm" c="dimmed">Email: {f.email}</Text>}
                  {f.phone && <Text size="sm" c="dimmed">Phone: {f.phone}</Text>}
                </Stack>

                {/* Remove button */}
                <Group justify="flex-end" mt="md">
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => handleRemoveFacilitator(f)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </>
  );
}